import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { FaGlobe, FaFacebook, FaYoutube, FaTwitter, FaInstagram, FaLinkedin, FaTiktok } from 'react-icons/fa';
import Modal from '../common/Modal/Modal';
import Notification from '../Notification/Notification';
import NotificationMenu from '../NotificationMenu/NotificationMenu';
import './Header.css';
import { GuestCounter } from '../GuestMode/components/GuestCounter/GuestCounter';
import { GuestGetMoreGuestsModal } from '../GuestMode/components/GuestGetMoreGuestsModal';
import { GuestModeModal } from '../GuestMode/components/GuestModeModal';
import { fetchUserCountry } from '../GuestMode/form/utils/userCountryUtils';
import config from '../../dashboard_config.json';
import { version } from '../../../package.json';

const Header = ({
  dashboardCompanyName,
  dashboardAdminFirstName,
  dashboardAdminLastName,
  logout,
  focusedPrimary,
  guestsInfo,
  filteredOutputs,
  companyType,
  isAtLeastOneCorporateLicense,
  showNotification,
  handlePrimaryFocus,
  primaryUsers,
  getControllerInfo,
  fetchExportList,
  setDisplayedName,
  notificationsHistory,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  clearNotificationsHistory,
  ...props
}) => {
  const [guestModeModalState, setGuestModeModalState] = useState({
    open: false,
  });

  const [dropdownActive, setDropdownActive] = useState(false);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const headerUserAreaRef = useRef(null);
  const showProfileTab = config.dashboard.headerSettings.showProfile;

  const {
    showSocialIcons,
    omnistreamWebUrl,
    facebookUrl,
    youtubeUrl,
    instagramUrl,
    linkedInUrl,
    twitterUrl,
    tiktokUrl
  } = config.dashboard.headerSettings.socialIcons;

  const setLogoByCompanyType = {
    0: '/images/dashboard_logo_header.svg',
    1: '/images/controller_logo_header.svg',
  };

  const { guestsTotal, guestsTotalHistory, guestsRemain, guestsFreeTrialRemain, guestsMax, guestsCurrentCount } = guestsInfo;
  const isAvailableGetGuests = guestsRemain > 0 || guestsFreeTrialRemain > 0;
  const isGuestReachedLimit = guestsCurrentCount >= guestsMax;

  const [dataTargetGuestsModalValue, setDataTargetGuestsModalValue] = useState('#create-guest-mode-modal');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerUserAreaRef.current && !headerUserAreaRef.current.contains(event.target)) {
        closeDropdowns();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const determineModalValue = async () => {
      if (isAvailableGetGuests && isGuestReachedLimit) {
        setDataTargetGuestsModalValue('#guests-reach-limit-modal');
      } else if (!isAvailableGetGuests) {
        setDataTargetGuestsModalValue('#get-guest-mode-modal');
      } else {
        setDataTargetGuestsModalValue('#create-guest-mode-modal');
        let userCountry = localStorage.getItem('userCountry');
        if (!userCountry) {
          userCountry = await fetchUserCountry();
        }
      }
    };

    determineModalValue();
  }, [isAvailableGetGuests, isGuestReachedLimit]);

  const toggleDropdown = () => {
    setDropdownActive(!dropdownActive);
    if (!dropdownActive) setNotificationMenuOpen(false);
  };

  const toggleNotificationMenu = () => {
    setNotificationMenuOpen(!notificationMenuOpen);
    if (!notificationMenuOpen) setDropdownActive(false);
  };

  const closeDropdowns = () => {
    setDropdownActive(false);
    setNotificationMenuOpen(false);
  };

  return (
    <>
      <div className="premium-navbar">
        <div className="navbar-logo-container">
          <div className="omni-logo"
            style={{
              backgroundImage: `url(${setLogoByCompanyType[companyType] || '/images/logo.svg'})`,
            }}>
          </div>
        </div>

        {companyType === 0 && (
          <div className="premium-separator" />
        )}

        <ul className="premium-nav-links">
          {companyType === 0 && (
            <li className="premium-nav-item">
              <NavLink to="/dashboard" className="premium-nav-link" activeClassName="active">
                DASHBOARD
              </NavLink>
            </li>
          )}

          {companyType === 0 && (
            <li className="premium-nav-item">
              <NavLink to="/map" className="premium-nav-link" activeClassName="active">
                MAP
              </NavLink>
            </li>
          )}

          {companyType === 0 && isAtLeastOneCorporateLicense && (
            <>
              <li className="premium-nav-item" data-toggle="modal" data-target={dataTargetGuestsModalValue}>
                <div
                  className="create-guest-mode-modal-button premium-nav-link"
                  onClick={
                    dataTargetGuestsModalValue === '#create-guest-mode-modal'
                      ? () => setGuestModeModalState({ open: true })
                      : null
                  }
                >
                  INVITE GUEST
                </div>
              </li>

              <li className="premium-nav-item guest-counter-item">
                <GuestCounter
                  guestsTotal={guestsTotal}
                  guestsTotalHistory={guestsTotalHistory}
                  guestsRemain={guestsRemain}
                  guestsFreeTrialRemain={guestsFreeTrialRemain}
                  guestsMax={guestsMax}
                />
              </li>
            </>
          )}
        </ul>
        <div className="notification-container">
          <Notification {...props} />
        </div>
        <div className="user-container" ref={headerUserAreaRef}>
          <NotificationMenu
            notifications={notificationsHistory}
            onMarkAllRead={markAllNotificationsAsRead}
            onMarkAsRead={markNotificationAsRead}
            onClearAll={clearNotificationsHistory}
            isOpen={notificationMenuOpen}
            onToggle={toggleNotificationMenu}
          />
          <div className="user-info" onClick={toggleDropdown}>
            <div className="username-circle">{dashboardCompanyName.slice(0, 2).toUpperCase()}</div>
          </div>
          <div className={`dropdown-menu ${dropdownActive ? 'active' : ''}`} onMouseLeave={closeDropdowns}>
            <div className="dropdown-user-info" style={{ pointerEvents: 'none' }}>
              <div className="username-circle">{dashboardCompanyName.slice(0, 2).toUpperCase()}</div>
              <div className='company-name'>{dashboardCompanyName}</div>
              {/* {dashboardAdminFirstName} {dashboardAdminLastName} */} {/* uncomment to show First and last naeme */}
            </div>
            {/* <div className='companyName'>{dashboardCompanyName}</div> */} {/* company name below username */}
            <div className="separator" style={{ pointerEvents: 'none' }}></div>
            {showProfileTab && (
              <NavLink to="/account" className="dropdown-menu-item" onClick={closeDropdowns}>
                <i className="material-icons">person_outline</i>
                <span>Account Info</span>
              </NavLink>
            )}
            <NavLink to="/reports" className="dropdown-menu-item" onClick={closeDropdowns}>
              <i className="material-icons">description</i>
              <span>Reports</span>
            </NavLink>
            <div className="dropdown-menu-item" href="#" data-toggle="modal" data-target="#logout-modal">
              <span className="material-icons">logout</span>
              <span>Logout</span>
            </div>
            {showSocialIcons && (
              <div className="dropdown-menu-social-icons">
                <a href={omnistreamWebUrl} target="_blank" rel="noopener noreferrer">
                  <FaGlobe />
                </a>
                <a href={facebookUrl} target="_blank" rel="noopener noreferrer">
                  <FaFacebook />
                </a>
                <a href={youtubeUrl} target="_blank" rel="noopener noreferrer">
                  <FaYoutube />
                </a>
                <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
                  <FaInstagram />
                </a>
                <a href={linkedInUrl} target="_blank" rel="noopener noreferrer">
                  <FaLinkedin />
                </a>
                <a href={twitterUrl} target="_blank" rel="noopener noreferrer">
                  <FaTwitter />
                </a>
                <a href={tiktokUrl} target="_blank" rel="noopener noreferrer">
                  <FaTiktok />
                </a>
              </div>
            )}
            <div className="header-version-container">
              <div className="header-version-txt">
                Ver: {version}
              </div>
              <div className="header-site-txt">
                Site: {process.env.REACT_APP_HOSTNAME ? process.env.REACT_APP_HOSTNAME.split('.')[0].toUpperCase() : 'UNKNOWN'}
              </div>
            </div>
          </div>
        </div>
        {/* End of premium-navbar */}
      </div>

      <Modal
        uniqueId="logout-modal"
        headerTitle="Logout"
        containerClass="premium-logout-modal modal-dialog-centered"
        hideHeader={true}
        buttons={[
          { name: 'Logout', clickHandler: logout, className: 'premium-logout-btn-confirm' },
          { name: 'Cancel', className: 'premium-logout-btn-cancel' },
        ]}
        bodyClass="premium-modal-body"
      >
        <div className="logout-content logout-content-gap">
          <i className="material-icons logout-warning-icon">logout</i>
          <p>Are you sure you want to logout?</p>
        </div>
      </Modal>
      <Modal
        uniqueId="guests-reach-limit-modal"
        headerTitle={<span className="danger-title">Concurrent Invitations Limit Reached</span>}
        buttons={[{ name: 'Close', className: 'danger danger-submit-btn' }]}
        bodyClass="modal-body modal-body-guests-reach-limit"
      >
        Guest limit reached.
        <br />
        Please use existing guests before generating new ones.
      </Modal>
      <GuestGetMoreGuestsModal />
      <GuestModeModal
        guestModeModalState={guestModeModalState}
        setGuestModeModalState={setGuestModeModalState}
        filteredOutputs={filteredOutputs}
        guestsMax={guestsMax}
        showNotification={showNotification}
        dashboardCompanyName={dashboardCompanyName}
        handlePrimaryFocus={handlePrimaryFocus}
        primaryUsers={primaryUsers}
        focusedPrimary={focusedPrimary}
        getControllerInfo={getControllerInfo}
        fetchExportList={fetchExportList}
        setDisplayedName={setDisplayedName}
      />
    </>
  );
};

Header.propTypes = {
  dashboardCompanyName: PropTypes.string.isRequired,
  dashboardAdminFirstName: PropTypes.string.isRequired,
  dashboardAdminLastName: PropTypes.string.isRequired,
};

export default React.memo(Header);
